import { IFaceted, IFacetMapping, IFacet, pack } from '../Facet';
import { getItemsOperation } from '../../operations/getItemsOperation';
import { IItemCacheState, getCacheItem } from '../../store/reducers/itemCacheReducer';
import { IItemCache } from '../IItemCacheObserver';
import { DemandResolution, resolvedFacet } from '../../facets/DemandFacets';
import { mapObject, areShallowEqual } from '../../utilities/objects';
import { preferredKeyFacetFacet, isOperationHandlerRegisteredFacet } from '../../facets/OperationFacets';
import { KeyFacet } from '../KeyFacet';
import { ComputedItemFacet, IComputedItemFacetProps } from './ComputedItemFacet';
import { IControlFaceted } from '../../components/decorators/withControlHandler';
import { ControlFacet, controlTypeKeyFacet } from '../controls/ControlFacet';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { getOperationTypeKey } from '../../store/reducers/operationMapReducer';
import { is } from '@ms/utilities-objects/lib/is';
import { IAction } from '../../store/actions/createActionCreator';
import { createMemoizer } from '../../utilities/Memoizer';

const ERROR_ITEM_CACHE_DISPOSED =
  'The IItemCache instance may only be used within an invocation of `mapItemCacheToProps` ' +
  'passed to`connectToItemCache`.';

const memoizeGetCacheItem = createMemoizer((state: IItemCacheState) => {
  const items: {
    [itemKey: string]: IFaceted | undefined;
  } = {};

  return (itemKey: string) => {
    return (itemKey in items) ?
      items[itemKey] :
      (items[itemKey] = getCacheItem(state, itemKey));
  };
});

const memoizeGetOperationTypeKey = createMemoizer((keyFacet: KeyFacet<{}>) => {
  return getOperationTypeKey({ operationTypeFacet: getItemsOperation, keyFacet });
});

const memoizeGetControlTypeKey = createMemoizer((controlFacet: ControlFacet<{}, {}>) => {
  return createMemoizer((keyFacet: KeyFacet<{}>) => {
    return ControlFacet.getControlTypeKey({
      keyFacet: keyFacet,
      controlTypeFacet: controlFacet
    });
  });
});

const memoizeGetKeyFacet = createMemoizer((state: IItemCacheState) => {
  // Consming the current state so there is an object to bind to for garbage collection.
  const items: {
    [itemKey: string]: KeyFacet<{}>;
  } = {};

  return (itemKey: string) => {
    return (itemKey in items) ?
      items[itemKey] :
      (items[itemKey] = KeyFacet.get(itemKey));
  };
});

export interface IItemCacheDependencies {
  [itemKey: string]: IFaceted;
}

export interface IItemCacheDemands {
  [itemKey: string]: typeof getItemsOperation.type;
}

export interface IItemCacheParams {
  /**
   * The current state from which this observer will pull items and track dependencies.
   */
  state: IItemCacheState;
  /**
   * Previous dependencies against which to compare the current state for changes.
   */
  previousDependencies?: IItemCacheDependencies;
  /**
   * Previous demands for debouncing output.
   */
  previousDemands?: IItemCacheDemands;
  /**
   * Control handler overrides to use when resolving handlers.
   */
  controlHandlers?: IControlFaceted;
  /**
   * The store dispatcher that the observer will provide to selectors.
   */
  dispatch: IItemCacheDispatch;
  /**
   * An object to use to tag as the owner for the current item cache.
   * Can be used during debugging to set conditional breakpoints to capture usage
   * of item cache selectors for specific components.
   */
  owner?: {};
}

/**
* Implementation of the component which provides access to the item cache,
* while also recording what is demanded along with the state of all cache
* entries at the time they are demanded.
*/
export class ItemCacheObserver implements IItemCache {
  protected _owner!: {} | undefined;

  private _getCacheItem: (itemKey: string) => IFaceted | undefined;
  private _getKeyFacet: (itemKey: string) => KeyFacet<{}>;
  private _previousDependencies: IItemCacheDependencies | undefined;
  private _previousDemands: IItemCacheDemands | undefined;
  private _controlHandlers?: IControlFaceted;
  private _dispatch: IItemCacheDispatch;

  /**
   * Track disposal on an isolated object.
   * This prevents the 'dispatch' function from capturing the entire
   * ItemCacheObserver in its closure, which could cause a memory leak.
   */
  private _scope: {
    isDisposed?: boolean | undefined;
  };

  private _items: {
    [itemKey: string]: boolean;
  };

  private _demands: {
    [itemKey: string]: IFaceted<boolean>;
  };

  private _facets: {
    [itemKey: string]: IFaceted | undefined;
  };

  private _computations: {
    [itemKey: string]: IFaceted | undefined;
  };

  private _resolutions: {
    [itemKey: string]: IFaceted<DemandResolution>;
  };

  constructor(params: IItemCacheParams) {
    const {
      dispatch,
      state
    } = params;

    const scope = this._scope = { isDisposed: false };

    this._getCacheItem = memoizeGetCacheItem(state);
    this._getKeyFacet = memoizeGetKeyFacet(state);
    this._previousDemands = params.previousDemands;
    this._previousDependencies = params.previousDependencies;
    this._controlHandlers = params.controlHandlers;
    this._owner = params.owner;

    this._items = {};
    this._facets = {};
    this._computations = {};
    this._demands = {};
    this._resolutions = {};

    this._dispatch = <T extends IAction>(action: T) => {
      if (!scope.isDisposed) {
        throw new Error(
          'Attempted to invoke the store dispatch within a callback to an IItemCache. ' +
          'This could disrupt the ability to track dependencies and cause unwanted cascading updates. ' +
          'Instead, hold onto dispatch in a local variable and invoke it in an asynchronous callback, such as an event handler, ' +
          'or simply return it from the selector before using it.');
      }

      return dispatch(action);
    };
  }

  public get dispatch(): IItemCacheDispatch {
    if (this._scope.isDisposed) {
      throw new Error(
        'Attempted to access the store dispatch on an ItemCache outside of its completed callback. ' +
        'This can cause a memory leak because an ItemCache holds onto stale store state. ' +
        'To obtain the store dispatch, access it during the callback and store it in a local variable.');
    }

    return this._dispatch;
  }

  /**
   * Evaluates an item cache selector against this observer and returns its result,
   * recording all demands and dependencies.
   */
  public evaluate<TResult>(selector: (itemCache: IItemCache) => TResult): {
    result: TResult;
  } {
    return {
      result: selector(this)
    };
  }

  public demandItem<TFacets extends object>(
    mapping: IFacetMapping<TFacets>,
    itemKey: string | undefined): Partial<TFacets> {

    if (this._scope.isDisposed) {
      throw new Error(ERROR_ITEM_CACHE_DISPOSED);
    }

    const facets = {} as Partial<TFacets>;

    for (const name in mapping) {
      if (Object.prototype.hasOwnProperty.call(mapping, name)) {
        const facet = mapping[name];

        if (!facet) {
          throw new Error(
            `Mapping '${name}' was not passed a valid Facet or IFacet instance in demandItem.`
          );
        }

        facets[name] = this.demandItemFacet(facet, itemKey);
      }
    }

    return facets;
  }

  public demandItemFacet<TValue>(facet: IFacet<TValue>, itemKey: string | undefined): TValue | undefined {
    if (this._scope.isDisposed) {
      throw new Error(ERROR_ITEM_CACHE_DISPOSED);
    }

    if (facet instanceof ControlFacet && !controlTypeKeyFacet.matches(itemKey)) {
      return this._addControl(itemKey, facet);
    }

    if (facet instanceof ComputedItemFacet) {
      return this._addComputation(itemKey, facet);
    }

    if (!itemKey) {
      return undefined;
    }

    const item = this._addItem(itemKey);

    if (facet.id !== resolvedFacet.id) {
      // The resolved facet cannot itself be resolved.
      this._addResolution(itemKey, item, facet);
    }

    const value = this._addValue(itemKey, item, facet);

    if (!value && (facet instanceof KeyFacet)) {
      // If the facet is a KeyFacet, then do the caller a favor and parse the input key if possible.
      // This can assist in making a decision while waiting for the real item to resolve.
      if (facet.matches(itemKey)) {
        return facet.deserialize(itemKey);
      }
    }

    return value;
  }

  public isItemFacetResolved<TValue>(facet: IFacet<TValue>, itemKey: string | undefined): boolean {
    if (this._scope.isDisposed) {
      throw new Error(ERROR_ITEM_CACHE_DISPOSED);
    }

    if (!itemKey) {
      return false;
    }

    if (facet instanceof ComputedItemFacet) {
      // In order to determine if a computed facet is resolved, its selector function needs to
      // be executed using a child ItemCacheObserver and its demands should be tracked.
      // If any facet demanded by the selector is in an unresolved state for the given item key,
      // then the facet is unresolved.
      // The dependencies of the selector then need to be tracked in the calling ItemCacheObserver,
      // but the demands must not be issued.

      // Presently, since it is not practical to track the demands of a child ItemCacheObserver,
      // return false to ensure that isItemFacetResolved on a ComputedItemFacet is not relied upon.
      return false;
    }

    const item = this._addItem(itemKey);

    const demandResolution = this._addResolution(itemKey, item, facet);

    return demandResolution === DemandResolution.resolved;
  }

  public haveDependenciesChanged(): boolean {
    const previousDependencies = this._previousDependencies;

    if (!previousDependencies) {
      return true;
    }

    // This function can't use a general deep-comparer because facets should not be deep-compared, with
    // exception of resolvedFacet.
    for (const itemKey in previousDependencies) {
      if (Object.prototype.hasOwnProperty.call(previousDependencies, itemKey)) {
        const previousItem = previousDependencies[itemKey];

        // Memoize accesses from the cache for performance, since it reduces key-mapping work.
        const item = this._getCacheItem(itemKey);

        for (const facetId in previousItem) {
          if (Object.prototype.hasOwnProperty.call(previousItem, facetId)) {
            if (facetId === resolvedFacet.id) {
              const previousResolved = resolvedFacet.unpack(previousItem);
              const resolved = item && resolvedFacet.unpack(item);

              const previousDemands = previousResolved && previousResolved.demands;
              const demands = resolved && resolved.demands;

              if (previousDemands) {
                for (const resolvedFacetId in previousDemands) {
                  if (Object.prototype.hasOwnProperty.call(previousDemands, resolvedFacetId)) {
                    const previousResolution = previousDemands[resolvedFacetId];
                    const resolution = demands && demands[resolvedFacetId];

                    if (normalizeDemandResolution(previousResolution) !== normalizeDemandResolution(resolution)) {
                      // Instead of returning true, return diagnostics about what changed.
                      // This aids debuggability because it will reveal what triggered a call back to a selector function
                      // when walking the stack.
                      return {
                        [itemKey]: {
                          [facetId]: {
                            demands: {
                              [resolvedFacetId]: {
                                previous: previousResolution,
                                current: resolution
                              }
                            }
                          }
                        }
                      } as {} as boolean;
                    }
                  }
                }
              }
            } else {
              const previousFacet = previousItem[facetId];
              const facet = item && item[facetId];

              if (!is(previousFacet, facet)) {
                // Instead of returning true, return diagnostics about what changed.
                // This aids debuggability because it will reveal what triggered a call back to a selector function
                // when walking the stack.
                return {
                  [itemKey]: {
                    [facetId]: {
                      previous: previousFacet,
                      current: facet
                    }
                  }
                } as {} as boolean;
              }
            }
          }
        }
      }
    }

    return false;
  }

  /**
   * Marks this item cache observer as disposed so callers can't access it asynchronously.
   */
  public dispose(): void {
    this._scope.isDisposed = true;
  }

  /**
   * Gets the current snapshot of demands from this item cache observer,
   * if they have differed from previous demands.
   *
   * The goal here is to track which facets the caller has demanded, and
   * emit the payload for the getItems request.
   */
  public getDemands(): IItemCacheDemands {
    const demands = mapObject(
      this._demands,
      (facets: IFaceted, itemKey: string | number | symbol): typeof getItemsOperation.type => {
        return {
          demands: {
            ...pack({}, {}),
            ...mapObject(facets, (value: {}, facetId: string | number | symbol) => {
              return true;
            })
          }
        };
      }
    );

    const isDirty = !areShallowEqual(demands, this._previousDemands, (left: {
      demands: IFaceted<boolean>
    }, right: {
      demands: IFaceted<boolean>;
    }) => areShallowEqual(left.demands, right.demands));

    return isDirty || !this._previousDemands ? demands : this._previousDemands;
  }

  /**
   * Gets the current snapshot of dependencies from this item cache observer,
   * if they have differed from the previous state.
   *
   * The goal here is to to produce a mapping of all referenced
   * item keys to the current values of demanded facets,
   * along with the current resolution states of each referenced facet.
   * At the same time, compare the result against the previous dependencies
   * to determine if the previous dependencies may be reused.
   */
  public getDependencies(): IItemCacheDependencies {
    const dependencies = mapObject(
      this._items,
      (item: boolean, itemKey: string | number | symbol): IFaceted => {
        const facets = this._facets[itemKey as string] || pack({}, {});
        const resolutions = this._resolutions[itemKey as string] || pack({}, {});

        return {
          ...facets,
          ...resolvedFacet.pack({
            demands: resolutions
          })
        };
      }
    );

    const isDirty = !areShallowEqual(
      dependencies,
      this._previousDependencies,
      (left: {}, right: {}, itemKey: string) => {
        const {
          [resolvedFacet.id]: {
            demands: leftDemands
          },
          ...leftFacets
        } = left as IFaceted<{ demands: IFaceted }>;

        const {
          [resolvedFacet.id]: {
            demands: rightDemands
          },
          ...rightFacets
        } = right as IFaceted<{ demands: IFaceted }>;

        return areShallowEqual(leftDemands, rightDemands) && areShallowEqual(leftFacets, rightFacets);
      });

    return isDirty || !this._previousDependencies ? dependencies : this._previousDependencies;
  }

  /**
   * Adds a dependency on an item.
   */
  private _addItem(itemKey: string): IFaceted | undefined {
    const item = this._getCacheItem(itemKey);

    this._items[itemKey] = true;

    return item;
  }

  /**
   * Adds a dependency on a computed facet.
   */
  private _addComputation<TValue>(itemKey: string | undefined, facet: ComputedItemFacet<TValue>): TValue | undefined {
    const computations: Partial<IFaceted> = this._computations[itemKey || ''] || (this._computations[itemKey || ''] = pack({}, {}));

    const facetId = facet.id;

    if (facetId in computations) {
      // This computation has already been seen and its dependencies recorded.
      return computations[facetId] as TValue;
    }

    const props: IComputedItemFacetProps = {
      itemKey: itemKey || undefined
    };

    // Pre-bind the computation function to its props, creating a pure selector.
    // This will allow the result to be memoized against its dependencies later.
    const selector = (itemCache: IItemCache) => facet.compute(itemCache, props);

    // For the moment, add the computation's dependencies to this observer's dependencies.
    // In the future, the goal is to maximize re-use of computations across observer instances.
    const {
      result: value
    } = this.evaluate(selector);

    computations[facetId] = value;

    return value;
  }

  /**
   * Adds a dependency on a control handler accessed by the item cache selector.
   */
  private _addControl<TValue>(
    itemKey: string | undefined,
    facet: ControlFacet<{}, {}>): TValue | undefined {
    const item = itemKey && this._addItem(itemKey) || undefined;

    const keyFacet = itemKey && this._addPreferredKeyFacet(itemKey, item);

    const controlTypeKey = keyFacet && memoizeGetControlTypeKey(facet)(keyFacet);

    const controlRegistryItem = controlTypeKey && this._addItem(controlTypeKey) || undefined;

    const controlHandler = controlTypeKey && this._addValue(controlTypeKey, controlRegistryItem, facet) || undefined;

    const propsControlHandler = this._controlHandlers && facet.unpack(this._controlHandlers);

    return facet.compose(controlHandler, propsControlHandler) as {} as TValue | undefined;
  }

  /**
   * Adds a dependency and demand on a facet value.
   */
  private _addValue<TValue>(itemKey: string, item: IFaceted | undefined, facet: IFacet<TValue>): TValue | undefined {
    const facets: Partial<IFaceted> = this._facets[itemKey] || (this._facets[itemKey] = pack({}, {}));

    const existingDemands = this._demands[itemKey];

    const demands = existingDemands || (this._demands[itemKey] = pack({}, {}));

    if (!existingDemands) {
      // Also add a dependency on the getItems operation handler.
      this._addOperationHandler(itemKey, item);
    }

    const facetId = facet.id;

    const value = item && item[facetId] as TValue;

    facets[facetId] = value;

    if (facetId !== preferredKeyFacetFacet.id && facetId !== resolvedFacet.id) {
      demands[facetId] = true;
    }

    return value;
  }

  /**
   * For an item, ensures that a dependency is always set up for its assocated preferred `KeyFacet`.
   */
  private _addPreferredKeyFacet(itemKey: string, item: IFaceted | undefined): KeyFacet<{}> {
    const facets: Partial<IFaceted> = this._facets[itemKey] || (this._facets[itemKey] = pack({}, {}));

    const preferredKeyFacet = item && item[preferredKeyFacetFacet.id] as KeyFacet<{}>;

    facets[preferredKeyFacetFacet.id] = preferredKeyFacet;

    const preferredKey = preferredKeyFacet && item && item[preferredKeyFacet.id];

    if (preferredKeyFacet) {
      facets[preferredKeyFacet.id] = preferredKey;
    }

    return preferredKeyFacet || this._getKeyFacet(itemKey);
  }

  /**
   * For an item, ensures that the operation handler for the associated `getItemsOperation` queue
   * is tracked as a dependency. If a handler is registered, the selector will need to be evaluated
   * again so demands may be re-issued.
   */
  private _addOperationHandler(itemKey: string, item: IFaceted | undefined): void {
    // Add a dependency on the preferred KeyFacet and key for the item.
    const keyFacet = this._addPreferredKeyFacet(itemKey, item);

    const operationTypeKey = memoizeGetOperationTypeKey(keyFacet);

    const operationFacets: Partial<IFaceted> = this._facets[operationTypeKey] || (this._facets[operationTypeKey] = pack({}, {}));

    const operation = this._addItem(operationTypeKey);

    const isOperationHandlerRegistered = operation && operation[isOperationHandlerRegisteredFacet.id];

    operationFacets[isOperationHandlerRegisteredFacet.id] = isOperationHandlerRegistered;
  }

  /**
   * Adds a dependency on a facet resolution state.
   */
  private _addResolution(itemKey: string, item: IFaceted | undefined, facet: IFacet<{}>): DemandResolution | undefined {
    const resolutions = this._resolutions[itemKey] || (this._resolutions[itemKey] = pack({}, {}));

    const resolved = item && resolvedFacet.unpack(item);
    const demands = resolved && resolved.demands;

    const facetId = facet.id;

    const resolution = demands && demands[facetId];

    resolutions[facetId] = normalizeDemandResolution(resolution);

    return resolution;
  }
}

/**
 * Since the only resolution states which matter for cache refreshes are 'resolved' and 'unresolved',
 * this function 'normalizes' a demand into only one of those two states.
 */
function normalizeDemandResolution(demandResolution?: DemandResolution): DemandResolution {
  return demandResolution === DemandResolution.resolved ? DemandResolution.resolved : DemandResolution.unresolved;
}
